import { SuccessFetchListResponse } from "@/services/api";
import {
  DataTableFeatures,
  DataTableSettings,
  UseDataTableOptions,
} from "@/types/dataTableSettings";
import {
  InfiniteData,
  UseInfiniteQueryResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { PropsWithChildren, useCallback, useMemo } from "react";
import { ZodObject, ZodRawShape, ZodReadonly } from "zod";
import DataTableColumnOrderContext from "../data-table/components/DataTableColumnOrderContext";
import DataTableColumnPinningContext from "../data-table/components/DataTableColumnPinningContext";
import DataTableColumnSizingContext from "../data-table/components/DataTableColumnSizingContext";
import DataTableColumnVisibilityContext from "../data-table/components/DataTableColumnVisibilityContext";
import {
  DataTableSettingsContextProvider,
  defaultDataTableFeatures,
} from "../data-table/DataTableSettingsContext";
import useColumns from "../data-table/hooks/useColumns";
import useTable from "../data-table/hooks/useTable";
import { ListContent } from "./components/ListContent";
import { ListHeader } from "./components/ListHeader";
import { ListMenubar } from "./components/ListMenubar";
import { ListStatus } from "./components/ListStatus";
import { ListDirtyRowsStatus } from "./components/ListDirtyRowsStatus";
import { ListSelectedRowsStatus } from "./components/ListSelectedRowsStatus";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface ListProps<T> extends PropsWithChildren {
  schema: ZodReadonly<ZodObject<ZodRawShape>>;
  name: string;
  breadcrumbs?: BreadcrumbItem[];
  breadcrumbLabel?: string;
  translationKey?: string;
  tableFeatures?: DataTableFeatures;
  headerActions?: (t: TFunction) => React.ReactNode[];
  columnOverrides?: Record<string, ColumnDef<T, number>>;
  query:
    | UseInfiniteQueryResult<InfiniteData<SuccessFetchListResponse<T>>, unknown>
    | UseQueryResult<SuccessFetchListResponse<T>>;
  options: UseDataTableOptions<T>;
  dataMapper?: (data: unknown[]) => T[];
  pathBuilder?: (row: T) => string;
}

export const List = <T extends { id: string }>({
  name,
  schema,
  tableFeatures = defaultDataTableFeatures,
  columnOverrides,
  children,
  translationKey,
  query,
  options,
  dataMapper,
  pathBuilder,
}: ListProps<T>) => {
  const tableFeaturesMerged = useMemo(
    () => ({
      ...defaultDataTableFeatures,
      ...tableFeatures,
    }),
    [tableFeatures],
  );

  const columns = useColumns({
    name,
    translationKey,
    schema,
    overrides: columnOverrides,
    enableRowSelection: tableFeaturesMerged.enableRowSelection,
    enableSelectAll: tableFeaturesMerged.enableSelectAll,
  });

  const tableData = useMemo(() => {
    if (!query.data) return [];
    // Handle infinite query results
    let data: T[] = [];
    if ("pages" in query.data) {
      data = query.data.pages.flatMap(({ items }) => items);
    } else {
      data = query.data.items;
    }
    // Handle regular query results
    return dataMapper ? dataMapper(data ?? []) : data;
  }, [query.data, dataMapper]);

  const meta = useMemo(
    () => ({
      pathBuilder,
    }),
    [pathBuilder],
  );
  const getRowId = useCallback((row: T) => row.id, []);

  const {
    table,
    stateColumnVisibility,
    stateColumnSizing,
    stateColumnOrder,
    stateColumnPinning,
    initialColumnVisibility,
  } = useTable({
    name,
    data: tableData,
    enableSorting: tableFeaturesMerged.enableSorting,
    enableColumnPinning: tableFeaturesMerged.enableColumnPinning,
    enableFilters: tableFeaturesMerged.enableFilters,
    enableGrouping: tableFeaturesMerged.enableGrouping,
    groupBy: tableFeaturesMerged.groupBy,
    enableRowSelection: tableFeaturesMerged.enableRowSelection,
    meta,
    columns: columns as ColumnDef<unknown, number>[],
    initialColumnSizes: tableFeaturesMerged.initialColumnSizes,
    getRowId,
    initialColumnVisibility: tableFeaturesMerged.initialColumnVisibility,
    getRowProps: tableFeaturesMerged.getRowProps,
  });

  const rowsCount = useMemo(() => {
    return table.getRowCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacting to count change, do not remove
  }, [table, table.getRowCount()]);

  const firstVisibleColumnId = useMemo(() => {
    const visibleHeaders = table.getLeafHeaders();
    const firstDataColumn = visibleHeaders.find(
      (header) =>
        header.column.id !== "select" &&
        header.column.columnDef.meta?.allowColumnToBeALink !== false,
    );
    return firstDataColumn?.column.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- additional deps for triggering re-render
  }, [table, stateColumnOrder, stateColumnVisibility, stateColumnPinning]);

  const contextValue = useMemo(
    () =>
      ({
        features: tableFeaturesMerged,
        options,
        table,
        isResizing: !!table.getState().columnSizingInfo.isResizingColumn,
        firstVisibleColumnId,
        rowsCount,
      }) as DataTableSettings<T>,
    [tableFeaturesMerged, options, table, firstVisibleColumnId, rowsCount],
  );

  const columnSizing = useMemo(
    () => ({ columnSizing: stateColumnSizing }),
    [stateColumnSizing],
  );
  const columnOrder = useMemo(
    () => ({ columnOrder: stateColumnOrder }),
    [stateColumnOrder],
  );
  const columnPinning = useMemo(
    () => ({ columnPinning: stateColumnPinning }),
    [stateColumnPinning],
  );
  const columnVisibility = useMemo(
    () => ({
      columnVisibility: stateColumnVisibility,
      resetColumnVisibility: () => {
        table.setColumnVisibility(initialColumnVisibility);
      },
    }),
    [stateColumnVisibility, table, initialColumnVisibility],
  );

  return (
    <DataTableColumnSizingContext.Provider value={columnSizing}>
      <DataTableColumnOrderContext.Provider value={columnOrder}>
        <DataTableColumnPinningContext.Provider value={columnPinning}>
          <DataTableColumnVisibilityContext.Provider value={columnVisibility}>
            <DataTableSettingsContextProvider value={contextValue}>
              {children}
            </DataTableSettingsContextProvider>
          </DataTableColumnVisibilityContext.Provider>
        </DataTableColumnPinningContext.Provider>
      </DataTableColumnOrderContext.Provider>
    </DataTableColumnSizingContext.Provider>
  );
};

List.Header = ListHeader;
List.Menubar = ListMenubar;
List.Content = ListContent;
List.Status = ListStatus;
List.DirtyRowsStatus = ListDirtyRowsStatus;
List.SelectedRowsStatus = ListSelectedRowsStatus;

List.displayName = "List";
